import React, { useState, useRef, useEffect } from 'react';
import { Search, X, Clock, TrendingUp } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';
import { cn } from '../../lib/utils';
import { usePageContext, generateAutoTrackingId, pushGTMEvent } from '../../utils/tracking';

interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'project' | 'conversation' | 'report' | 'agent';
  url: string;
}

interface SearchInputProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  className?: string;
  // Tracking props
  trackingId?: string;
  trackingSection?: string;
  disableTracking?: boolean;
}

export function SearchInput({ 
  placeholder = "Search...", 
  onSearch, 
  className,
  trackingId,
  trackingSection,
  disableTracking = false 
}: SearchInputProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches] = useState<string[]>([
    'Website traffic last week',
    'Conversion rate analysis',
    'Marketing strategy Q2'
  ]);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { pageName, section } = usePageContext();

  // Generate tracking ID if not provided and tracking is enabled
  const autoTrackingId = !disableTracking && !trackingId ? 
    generateAutoTrackingId({
      elementType: 'input',
      pageName,
      section: trackingSection || section,
      placeholder,
      variant: 'search'
    }) : trackingId;

  // Mock search results
  const mockResults: SearchResult[] = [
    {
      id: '1',
      title: 'E-commerce Website',
      description: 'Main company website analytics',
      type: 'project',
      url: '/projects/1'
    },
    {
      id: '2',
      title: 'Traffic Analysis Conversation',
      description: 'Discussion about website traffic trends',
      type: 'conversation',
      url: '/agents/data/conversation/2'
    },
    {
      id: '3',
      title: 'Monthly Marketing Report',
      description: 'Comprehensive monthly overview',
      type: 'report',
      url: '/reports/3'
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (query.length > 0) {
      // Simulate search API call
      const filteredResults = mockResults.filter(result =>
        result.title.toLowerCase().includes(query.toLowerCase()) ||
        result.description.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [query]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(true);
    
    // Track search event
    if (!disableTracking && autoTrackingId && value.length > 0) {
      pushGTMEvent({
        elementType: 'input',
        elementId: autoTrackingId,
        elementText: value,
        pageName,
        section: trackingSection || section,
        variant: 'search',
        userAction: 'change'
      });
    }
    
    onSearch?.(value);
  };

  const handleClear = () => {
    // Track clear event
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'button',
        elementId: `${autoTrackingId}-clear`,
        elementText: 'clear search',
        pageName,
        section: trackingSection || section,
        variant: 'ghost',
        userAction: 'click'
      });
    }
    
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  const handleRecentSearchClick = (search: string) => {
    // Track recent search selection
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'button',
        elementId: `${autoTrackingId}-recent-search`,
        elementText: search,
        pageName,
        section: trackingSection || section,
        variant: 'recent',
        userAction: 'click'
      });
    }
    
    setQuery(search);
    onSearch?.(search);
  };

  const handleResultClick = (result: SearchResult) => {
    // Track search result click
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'link',
        elementId: `${autoTrackingId}-result`,
        elementText: result.title,
        pageName,
        section: trackingSection || section,
        variant: result.type,
        userAction: 'click'
      });
    }
  };

  const handleQuickActionClick = (action: string) => {
    // Track quick action click
    if (!disableTracking && autoTrackingId) {
      pushGTMEvent({
        elementType: 'button',
        elementId: `${autoTrackingId}-quick-action`,
        elementText: action,
        pageName,
        section: trackingSection || section,
        variant: 'quick-action',
        userAction: 'click'
      });
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'project': return '📁';
      case 'conversation': return '💬';
      case 'report': return '📊';
      case 'agent': return '🤖';
      default: return '📄';
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
        <Input
          ref={inputRef}
          value={query}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="pl-10 pr-10"
          trackingId={autoTrackingId}
          trackingSection={trackingSection}
          disableTracking={disableTracking}
        />
        {query && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClear}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
            trackingId={`${autoTrackingId}-clear-button`}
            trackingSection={trackingSection}
            disableTracking={disableTracking}
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Search Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {query.length === 0 ? (
            /* Recent Searches */
            <div className="p-3">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                Recent Searches
              </h4>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleRecentSearchClick(search)}
                  className="w-full text-left px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                >
                  {search}
                </button>
              ))}
            </div>
          ) : results.length > 0 ? (
            /* Search Results */
            <div className="p-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2">
                Results for "{query}"
              </h4>
              {results.map((result) => (
                <a
                  key={result.id}
                  href={result.url}
                  onClick={() => handleResultClick(result)}
                  className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                >
                  <span className="text-lg mr-3">{getTypeIcon(result.type)}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{result.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{result.description}</p>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">{result.type}</span>
                </a>
              ))}
            </div>
          ) : (
            /* No Results */
            <div className="p-6 text-center">
              <Search className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No results found for "{query}"</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Try adjusting your search terms</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-2">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-2 flex items-center">
              <TrendingUp className="w-3 h-3 mr-1" />
              Quick Actions
            </h4>
            <div className="grid grid-cols-2 gap-1">
              <button 
                onClick={() => handleQuickActionClick('Ask AI Agent')}
                className="text-left px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
              >
                Ask AI Agent
              </button>
              <button 
                onClick={() => handleQuickActionClick('Create Report')}
                className="text-left px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
              >
                Create Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}